import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FiRefreshCw,
  FiChevronDown,
  FiChevronRight,
  FiTarget,
  FiCheckCircle,
  FiFilter,
  FiInfo,
  FiSearch,
} from 'react-icons/fi';
import { SiFirebase } from 'react-icons/si';
import { IconType } from 'react-icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { useSearchParams } from 'react-router-dom';
import { dashboardService } from '../api/services/dashboardService';
import {
  fetchRoadmapProgressPage,
  fetchRoadmapProgressByPlayerId,
  fetchAllRoadmapNewPlayers,
  ROADMAP_PROGRESS_PAGE_SIZE,
} from '../api/services/firestoreRoadmapProgress';
import {
  RoadmapItem,
  RoadmapDetailItem,
  RoadmapDetailMeta,
  RoadmapFirestoreEvent,
  RoadmapProgressPlayer,
  RoadmapProgressTask,
} from '../api/types';
import { getUserFriendlyErrorMessage } from '../api/utils/errorHandler';
import { cn } from '../lib/utils';
import {
  apiRoadmapFiltersActive,
  buildRoadmapListParams,
  ROADMAP_LIST_PAGE_SIZE,
  STALE_INACTIVE_DAYS,
  USER_TYPE_FILTER_OPTIONS,
  type ApiRoadmapFilterCfg,
} from './roadmap/buildRoadmapListParams';
import { RoadmapApiFiltersPanel } from './roadmap/RoadmapApiFiltersPanel';

type RoadmapFilterDropdownOption = { value: string; label: string };

const ChevronDownIcon = FiChevronDown as React.ComponentType<{ className?: string }>;

type RoadmapFilterDropdownProps = {
  label: string;
  labelId: string;
  value: string;
  onChange: (value: string) => void;
  options: RoadmapFilterDropdownOption[];
  placeholder: string;
};

/** Native select yerine — Platform / Roadmap filtreleri için tutarlı açılır menü. */
function RoadmapFilterDropdown({
  label,
  labelId,
  value,
  onChange,
  options,
  placeholder,
}: RoadmapFilterDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = options.find((o) => o.value === value);
  const displayText = selected?.label ?? placeholder;

  return (
    <div className="space-y-1.5" ref={wrapRef}>
      <span
        id={`${labelId}-caption`}
        className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
      >
        {label}
      </span>
      <div className="relative">
        <button
          type="button"
          id={labelId}
          aria-labelledby={`${labelId}-caption`}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className={cn(
            'flex h-[42px] w-full items-center justify-between gap-2 rounded-xl border px-3.5 text-left text-sm shadow-sm transition-all',
            'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50/90',
            'dark:border-slate-600 dark:bg-gray-800 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-gray-800/90',
            open &&
              'border-amber-500 shadow-md shadow-amber-500/10 ring-2 ring-amber-500/20 dark:border-amber-500 dark:ring-amber-500/25'
          )}
        >
          <span className="min-w-0 flex-1 truncate font-medium">{displayText}</span>
          <ChevronDownIcon
            className={cn(
              'h-4 w-4 shrink-0 text-slate-400 transition-transform duration-200 dark:text-slate-500',
              open && 'rotate-180 text-amber-600 dark:text-amber-400'
            )}
          />
        </button>
        {open && (
          <ul
            role="listbox"
            aria-labelledby={`${labelId}-caption`}
            className="absolute left-0 right-0 top-[calc(100%+6px)] z-[60] max-h-56 overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-xl shadow-slate-900/[0.08] ring-1 ring-slate-900/[0.04] dark:border-slate-600 dark:bg-gray-900 dark:shadow-black/50 dark:ring-white/[0.06]"
          >
            {options.map((opt) => {
              const active = opt.value === value;
              return (
                <li key={`${labelId}-${opt.value || '__empty'}`} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => {
                      onChange(opt.value);
                      setOpen(false);
                    }}
                    className={cn(
                      'flex w-full px-3 py-2.5 text-left text-sm transition-colors',
                      active
                        ? 'bg-gradient-to-r from-amber-50 to-amber-50/50 font-semibold text-amber-950 dark:from-amber-950/50 dark:to-transparent dark:text-amber-50'
                        : 'text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-gray-800/90'
                    )}
                  >
                    {opt.label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

const ROADMAP_SOURCE_FILTER_OPTIONS: RoadmapFilterDropdownOption[] = [
  { value: 'all', label: 'Tüm kayıtlar' },
  { value: 'yes', label: 'Sadece Roadmap: Evet (Hayır gizle)' },
  { value: 'no', label: 'Sadece Roadmap: Hayır (Evet gizle)' },
];

/** Roadmap'ten (Evet) görevlerde en yüksek origin — filtre ve kart özeti için. */
function maxOriginFromRoadmapTasks(p: RoadmapProgressPlayer): number {
  const rm = p.completed_tasks.filter((t) => t.completed_from_roadmap === true);
  if (!rm.length) return 0;
  return Math.max(0, ...rm.map((t) => t.origin ?? 0));
}

function dayStartMs(iso: string): number | null {
  const t = iso?.trim();
  if (!t) return null;
  const d = new Date(`${t}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function dayEndMs(iso: string): number | null {
  const t = iso?.trim();
  if (!t) return null;
  const d = new Date(`${t}T23:59:59.999`);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** Görev `completed_at` yerel gün aralığında mı? (biri boşsa tek uçlu filtre.) */
function eventCompletedAtInRange(
  completedAt: Date | null | undefined,
  fromIso: string,
  toIso: string
): boolean {
  const fromMs = dayStartMs(fromIso);
  const toMs = dayEndMs(toIso);
  if (fromMs == null && toMs == null) return true;
  if (!completedAt) return false;
  const x = completedAt.getTime();
  if (fromMs != null && x < fromMs) return false;
  if (toMs != null && x > toMs) return false;
  return true;
}

function filterEventsByCompletedAtRange(
  events: RoadmapFirestoreEvent[],
  fromIso: string,
  toIso: string
): RoadmapFirestoreEvent[] {
  if (!fromIso.trim() && !toIso.trim()) return events;
  return events.filter((e) => eventCompletedAtInRange(e.completed_at, fromIso, toIso));
}

/** Kart / tabloda: Evet modunda tüm “Hayır” satırlarını, Hayır modunda tüm “Evet” satırlarını göstermez. */
function filterEventsByRoadmapSource(
  events: RoadmapFirestoreEvent[],
  mode: 'all' | 'yes' | 'no'
): RoadmapFirestoreEvent[] {
  if (mode === 'all') return events;
  if (mode === 'yes') return events.filter((e) => e.completed_from_roadmap === true);
  return events.filter((e) => e.completed_from_roadmap === false);
}

function playerHasTaskInDateRange(p: RoadmapProgressPlayer, fromIso: string, toIso: string): boolean {
  if (!fromIso.trim() && !toIso.trim()) return true;
  return p.completed_tasks.some((t) => eventCompletedAtInRange(t.completed_at, fromIso, toIso));
}

const STALE_INACTIVE_MS = STALE_INACTIVE_DAYS * 24 * 60 * 60 * 1000;

/** Son görev `completed_at` en büyüğü; görev yoksa döküman `created_at`. */
function lastPlayerActivityMs(p: RoadmapProgressPlayer): number {
  const fromTasks = p.completed_tasks.length
    ? Math.max(0, ...p.completed_tasks.map((t) => t.completed_at?.getTime() ?? 0))
    : 0;
  if (fromTasks > 0) return fromTasks;
  return p.created_at?.getTime() ?? 0;
}

/** Son aktiviteden bu yana ≥ STALE_INACTIVE_DAYS gün (tarih yoksa uyarılı sayılır). */
function isPlayerInactive10Days(p: RoadmapProgressPlayer, nowMs: number = Date.now()): boolean {
  const last = lastPlayerActivityMs(p);
  if (last <= 0) return true;
  return nowMs - last >= STALE_INACTIVE_MS;
}

type FirebasePlayerFilterCfg = {
  platform: string;
  roadmap: 'all' | 'yes' | 'no';
  minOrigin: string;
  dateFrom: string;
  dateTo: string;
  staleOnly: boolean;
};

function firebasePlayerFiltersActive(f: FirebasePlayerFilterCfg): boolean {
  return (
    !!f.platform.trim() ||
    f.roadmap !== 'all' ||
    !!f.minOrigin.trim() ||
    !!f.dateFrom.trim() ||
    !!f.dateTo.trim() ||
    f.staleOnly
  );
}

function applyFirebasePlayerFilters(list: RoadmapProgressPlayer[], f: FirebasePlayerFilterCfg): RoadmapProgressPlayer[] {
  let out = list;
  const platform = f.platform.trim();
  if (platform) {
    out = out.filter((p) => (p.platform || '').trim() === platform);
  }
  if (f.roadmap === 'yes') {
    out = out.filter((p) => p.completed_tasks.some((t) => t.completed_from_roadmap === true));
  } else if (f.roadmap === 'no') {
    out = out.filter((p) => p.completed_tasks.some((t) => t.completed_from_roadmap === false));
  }
  const minStr = f.minOrigin.trim();
  if (minStr !== '') {
    const minO = parseInt(minStr, 10);
    if (!Number.isNaN(minO)) {
      out = out.filter((p) => maxOriginFromRoadmapTasks(p) >= minO);
    }
  }
  if (f.dateFrom.trim() || f.dateTo.trim()) {
    out = out.filter((p) => playerHasTaskInDateRange(p, f.dateFrom, f.dateTo));
  }
  if (f.staleOnly) {
    out = out.filter((p) => isPlayerInactive10Days(p));
  }
  return out;
}

function eventFromProgressTask(
  p: RoadmapProgressPlayer,
  t: RoadmapProgressTask
): RoadmapFirestoreEvent {
  return {
    player_id: p.player_id || null,
    task_code: t.task_code || null,
    task_title: t.task_title || null,
    task_type: t.task_type || null,
    task_scene: t.task_scene || null,
    quiz_id: t.quiz_id || null,
    chatbot_interaction_type: t.chatbot_interaction_type || null,
    required_level: t.required_level,
    session_no: t.session_no,
    session_title: t.session_title || null,
    completed_from_roadmap: t.completed_from_roadmap,
    origin: t.origin,
    destination: t.destination,
    platform: p.platform || null,
    app_version: p.app_version || null,
    completed_at: t.completed_at,
  };
}

const FB_PAGE = 'fbPage';
const FB_PLAYER = 'fbPlayer';
const ROADMAP_FIREBASE_UI_ENABLED = false;

const RoadMap: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [roadmapItems, setRoadmapItems] = useState<RoadmapItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());
  const [playerDetails, setPlayerDetails] = useState<Record<string, RoadmapDetailItem[]>>({});
  const [loadingDetails, setLoadingDetails] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
  });

  const [apiFilterPlatform, setApiFilterPlatform] = useState('');
  const [apiFilterRoadmap, setApiFilterRoadmap] = useState<'all' | 'yes' | 'no'>('all');
  const [apiFilterMinOrigin, setApiFilterMinOrigin] = useState('');
  const [apiFilterDateFrom, setApiFilterDateFrom] = useState('');
  const [apiFilterDateTo, setApiFilterDateTo] = useState('');
  const [apiFilterStaleOnly, setApiFilterStaleOnly] = useState(false);
  const [apiFilterUserType, setApiFilterUserType] = useState('');
  const [apiFilterPlayerId, setApiFilterPlayerId] = useState('');
  const [apiPlayerSearchInput, setApiPlayerSearchInput] = useState('');

  const apiFilterCfg = useMemo<ApiRoadmapFilterCfg>(
    () => ({
      platform: apiFilterPlatform,
      roadmap: apiFilterRoadmap,
      minOrigin: apiFilterMinOrigin,
      dateFrom: apiFilterDateFrom,
      dateTo: apiFilterDateTo,
      staleOnly: apiFilterStaleOnly,
      playerId: apiFilterPlayerId,
      userType: apiFilterUserType,
    }),
    [
      apiFilterPlatform,
      apiFilterRoadmap,
      apiFilterMinOrigin,
      apiFilterDateFrom,
      apiFilterDateTo,
      apiFilterStaleOnly,
      apiFilterPlayerId,
      apiFilterUserType,
    ]
  );

  const apiFiltersActive = apiRoadmapFiltersActive(apiFilterCfg);

  const [progressPlayers, setProgressPlayers] = useState<RoadmapProgressPlayer[]>([]);
  const [firebaseViewActive, setFirebaseViewActive] = useState(false);
  const [firebaseHasNextPage, setFirebaseHasNextPage] = useState(false);
  const [firebaseListLoading, setFirebaseListLoading] = useState(false);
  /** Filtre açıkken tüm `New` ham liste; null = sunucu sayfa modu. */
  const [firebaseAllNewCache, setFirebaseAllNewCache] = useState<RoadmapProgressPlayer[] | null>(null);
  const [firebaseClientFilteredTotal, setFirebaseClientFilteredTotal] = useState(0);
  const [firebaseReloadNonce, setFirebaseReloadNonce] = useState(0);
  const [firebasePlayerSearchInput, setFirebasePlayerSearchInput] = useState('');
  const [firebaseError, setFirebaseError] = useState<string | null>(null);
  const [expandedFirebasePlayers, setExpandedFirebasePlayers] = useState<Set<string>>(new Set());
  const firebaseUrlKey = searchParams.toString();
  const firebasePageFromUrl = Math.max(1, parseInt(searchParams.get(FB_PAGE) || '1', 10) || 1);
  const firebasePlayerFromUrl = (searchParams.get(FB_PLAYER) || '').trim();

  const [firebaseFilterPlatform, setFirebaseFilterPlatform] = useState('');
  const [firebaseFilterRoadmap, setFirebaseFilterRoadmap] = useState<'all' | 'yes' | 'no'>('all');
  const [firebaseFilterMinRoadmapOrigin, setFirebaseFilterMinRoadmapOrigin] = useState('');
  const [firebaseFilterDateFrom, setFirebaseFilterDateFrom] = useState('');
  const [firebaseFilterDateTo, setFirebaseFilterDateTo] = useState('');
  const [firebaseFilterStaleOnly, setFirebaseFilterStaleOnly] = useState(false);

  const firebaseFilterCfg = useMemo<FirebasePlayerFilterCfg>(
    () => ({
      platform: firebaseFilterPlatform,
      roadmap: firebaseFilterRoadmap,
      minOrigin: firebaseFilterMinRoadmapOrigin,
      dateFrom: firebaseFilterDateFrom,
      dateTo: firebaseFilterDateTo,
      staleOnly: firebaseFilterStaleOnly,
    }),
    [
      firebaseFilterPlatform,
      firebaseFilterRoadmap,
      firebaseFilterMinRoadmapOrigin,
      firebaseFilterDateFrom,
      firebaseFilterDateTo,
      firebaseFilterStaleOnly,
    ]
  );

  const firebaseEvents = useMemo((): RoadmapFirestoreEvent[] => {
    const out: RoadmapFirestoreEvent[] = [];
    for (const p of progressPlayers) {
      for (const t of p.completed_tasks) {
        out.push(eventFromProgressTask(p, t));
      }
    }
    return out;
  }, [progressPlayers]);

  const firebaseStats = useMemo(() => {
    const events = filterEventsByRoadmapSource(firebaseEvents, firebaseFilterRoadmap);
    const fromRoadMap = events.filter((e) => e.completed_from_roadmap === true).length;
    const fromOutside = events.filter((e) => e.completed_from_roadmap === false).length;
    const unknownOrigin = events.length - fromRoadMap - fromOutside;
    return {
      uniquePlayers: progressPlayers.length,
      fromRoadMap,
      fromOutside,
      unknownOrigin,
      total: events.length,
    };
  }, [firebaseEvents, progressPlayers.length, firebaseFilterRoadmap]);

  const firebaseByPlayer = useMemo(() => {
    const map = new Map<string, RoadmapFirestoreEvent[]>();
    for (const p of progressPlayers) {
      const evs: RoadmapFirestoreEvent[] = p.completed_tasks.map((t) => eventFromProgressTask(p, t));
      evs.sort(
        (a, b) => (a.completed_at?.getTime() ?? 0) - (b.completed_at?.getTime() ?? 0)
      );
      map.set(p.player_id, evs);
    }
    return map;
  }, [progressPlayers]);

  const firebasePlatformOptions = useMemo(() => {
    const set = new Set<string>();
    const src = firebaseAllNewCache ?? progressPlayers;
    for (const p of src) {
      const plat = (p.platform || '').trim();
      if (plat) set.add(plat);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'tr'));
  }, [firebaseAllNewCache, progressPlayers]);

  const isFirebaseClientListMode =
    firebaseAllNewCache !== null &&
    !firebasePlayerFromUrl &&
    firebasePlayerFiltersActive(firebaseFilterCfg);

  const filteredProgressPlayers = useMemo(() => {
    if (isFirebaseClientListMode) {
      return progressPlayers;
    }
    return applyFirebasePlayerFilters(progressPlayers, firebaseFilterCfg);
  }, [isFirebaseClientListMode, progressPlayers, firebaseFilterCfg]);

  /** Filtreler ne olursa olsun: en son aktivitesi (görev veya kayıt) en yeni olan üstte. */
  const progressPlayersSorted = useMemo(
    () => [...filteredProgressPlayers].sort((a, b) => lastPlayerActivityMs(b) - lastPlayerActivityMs(a)),
    [filteredProgressPlayers]
  );

  const firebaseFiltersActive = firebasePlayerFiltersActive(firebaseFilterCfg);

  const firebaseClientTotalPages = useMemo(() => {
    if (!isFirebaseClientListMode) return null;
    return Math.max(1, Math.ceil(firebaseClientFilteredTotal / ROADMAP_PROGRESS_PAGE_SIZE));
  }, [isFirebaseClientListMode, firebaseClientFilteredTotal]);

  useEffect(() => {
    if (!ROADMAP_FIREBASE_UI_ENABLED) return;
    if (!firebaseViewActive || firebasePlayerFromUrl) return;
    if (firebaseClientTotalPages == null) return;
    if (firebasePageFromUrl <= firebaseClientTotalPages) return;
    const p = firebaseClientTotalPages;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set(FB_PAGE, String(p));
        n.delete(FB_PLAYER);
        return n;
      },
      { replace: true }
    );
  }, [
    firebaseViewActive,
    firebasePlayerFromUrl,
    firebaseClientTotalPages,
    firebasePageFromUrl,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!ROADMAP_FIREBASE_UI_ENABLED) {
      const fp = searchParams.get(FB_PAGE);
      const pid = (searchParams.get(FB_PLAYER) || '').trim();
      if (pid || (fp != null && fp !== '')) {
        setFirebaseViewActive(false);
        setFirebaseListLoading(false);
        setFirebaseError(null);
        setProgressPlayers([]);
        setFirebaseAllNewCache(null);
        setSearchParams(
          (prev) => {
            const n = new URLSearchParams(prev);
            n.delete(FB_PAGE);
            n.delete(FB_PLAYER);
            return n;
          },
          { replace: true }
        );
      }
      return;
    }
    const pid = (searchParams.get(FB_PLAYER) || '').trim();
    const fp = searchParams.get(FB_PAGE);
    if (pid || (fp != null && fp !== '')) {
      setFirebaseViewActive(true);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!ROADMAP_FIREBASE_UI_ENABLED || !firebaseViewActive) return;
    const params = new URLSearchParams(firebaseUrlKey);
    const pid = (params.get(FB_PLAYER) || '').trim();
    const page = Math.max(1, parseInt(params.get(FB_PAGE) || '1', 10) || 1);
    let cancelled = false;

    if (pid) {
      setFirebaseAllNewCache(null);
      setFirebaseListLoading(true);
      setFirebaseError(null);
      (async () => {
        try {
          const one = await fetchRoadmapProgressByPlayerId(pid);
          if (cancelled) return;
          setProgressPlayers(one ? [one] : []);
          setFirebaseHasNextPage(false);
          setFirebaseClientFilteredTotal(one ? 1 : 0);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Firestore isteği başarısız';
          if (!cancelled) {
            setFirebaseError(message);
            setProgressPlayers([]);
            setFirebaseHasNextPage(false);
            setFirebaseClientFilteredTotal(0);
          }
          console.error('Firestore roadmap_progress:', err);
        } finally {
          if (!cancelled) setFirebaseListLoading(false);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    const needClientScan = firebasePlayerFiltersActive(firebaseFilterCfg);

    (async () => {
      if (cancelled) return;
      if (!needClientScan) {
        setFirebaseListLoading(true);
        setFirebaseError(null);
        try {
          setFirebaseAllNewCache(null);
          const { players, hasNextPage } = await fetchRoadmapProgressPage(page);
          if (cancelled) return;
          setProgressPlayers(players);
          setFirebaseHasNextPage(hasNextPage);
          setFirebaseClientFilteredTotal(0);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Firestore isteği başarısız';
          if (!cancelled) {
            setFirebaseError(message);
            setProgressPlayers([]);
            setFirebaseHasNextPage(false);
          }
          console.error('Firestore roadmap_progress:', err);
        } finally {
          if (!cancelled) setFirebaseListLoading(false);
        }
        return;
      }

      setFirebaseListLoading(true);
      setFirebaseError(null);
      try {
        let raw = firebaseAllNewCache;
        if (!raw) {
          raw = await fetchAllRoadmapNewPlayers();
          if (cancelled) return;
          setFirebaseAllNewCache(raw);
        }
        const filtered = applyFirebasePlayerFilters(raw, firebaseFilterCfg);
        const sorted = [...filtered].sort((a, b) => lastPlayerActivityMs(b) - lastPlayerActivityMs(a));
        const start = (page - 1) * ROADMAP_PROGRESS_PAGE_SIZE;
        const slice = sorted.slice(start, start + ROADMAP_PROGRESS_PAGE_SIZE);
        if (cancelled) return;
        setProgressPlayers(slice);
        setFirebaseHasNextPage(page * ROADMAP_PROGRESS_PAGE_SIZE < sorted.length);
        setFirebaseClientFilteredTotal(sorted.length);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Firestore isteği başarısız';
        if (!cancelled) {
          setFirebaseError(message);
          setProgressPlayers([]);
          setFirebaseHasNextPage(false);
          setFirebaseClientFilteredTotal(0);
        }
        console.error('Firestore roadmap_progress:', err);
      } finally {
        if (!cancelled) setFirebaseListLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    firebaseViewActive,
    firebaseUrlKey,
    firebasePlayerFromUrl,
    firebasePageFromUrl,
    firebaseFilterCfg,
    firebaseAllNewCache,
    firebaseReloadNonce,
  ]);

  useEffect(() => {
    setFirebasePlayerSearchInput(firebasePlayerFromUrl);
  }, [firebasePlayerFromUrl]);

  const handleFetchFirebase = () => {
    if (!ROADMAP_FIREBASE_UI_ENABLED) return;
    const next = new URLSearchParams(searchParams);
    if (!next.get(FB_PLAYER) && !next.get(FB_PAGE)) {
      next.set(FB_PAGE, '1');
    }
    const same = next.toString() === searchParams.toString();
    setFirebaseViewActive(true);
    setSearchParams(next, { replace: true });
    if (same) {
      setFirebaseAllNewCache(null);
      setFirebaseReloadNonce((n) => n + 1);
    }
  };

  const setFirebasePageUrl = (page: number) => {
    const p = Math.max(1, page);
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set(FB_PAGE, String(p));
        n.delete(FB_PLAYER);
        return n;
      },
      { replace: true }
    );
  };

  const submitFirebasePlayerSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = firebasePlayerSearchInput.trim();
    if (!q) return;
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.set(FB_PLAYER, q);
        n.delete(FB_PAGE);
        return n;
      },
      { replace: true }
    );
  };

  const clearFirebasePlayerSearch = () => {
    setFirebasePlayerSearchInput('');
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete(FB_PLAYER);
        if (!n.get(FB_PAGE)) n.set(FB_PAGE, '1');
        return n;
      },
      { replace: true }
    );
  };

  const clearFirebaseView = () => {
    setSearchParams(
      (prev) => {
        const n = new URLSearchParams(prev);
        n.delete(FB_PAGE);
        n.delete(FB_PLAYER);
        return n;
      },
      { replace: true }
    );
    setFirebaseViewActive(false);
    setProgressPlayers([]);
    setFirebaseHasNextPage(false);
    setFirebaseAllNewCache(null);
    setFirebaseClientFilteredTotal(0);
    setFirebaseReloadNonce(0);
    setFirebaseError(null);
    setExpandedFirebasePlayers(new Set());
    setFirebasePlayerSearchInput('');
    setFirebaseFilterPlatform('');
    setFirebaseFilterRoadmap('all');
    setFirebaseFilterMinRoadmapOrigin('');
    setFirebaseFilterDateFrom('');
    setFirebaseFilterDateTo('');
    setFirebaseFilterStaleOnly(false);
  };

  const resetFirebaseFilters = () => {
    setFirebaseFilterPlatform('');
    setFirebaseFilterRoadmap('all');
    setFirebaseFilterMinRoadmapOrigin('');
    setFirebaseFilterDateFrom('');
    setFirebaseFilterDateTo('');
    setFirebaseFilterStaleOnly(false);
  };

  const renderIcon = (Icon: IconType, className: string) => {
    const IconComponent = Icon as React.ComponentType<{ className?: string }>;
    return <IconComponent className={className} />;
  };

  const fetchRoadMapData = async (page: number = 1, filters: ApiRoadmapFilterCfg = apiFilterCfg) => {
    try {
      setLoading(true);
      setError(null);

      const response = await dashboardService.getRoadmapList(buildRoadmapListParams(page, filters));

      setRoadmapItems(response.results || []);
      const total = response.count || 0;
      setPagination({
        total,
        totalPages: Math.max(1, Math.ceil(total / ROADMAP_LIST_PAGE_SIZE)),
      });
    } catch (err: any) {
      const errorMessage = getUserFriendlyErrorMessage(err);
      setError(errorMessage);
      console.error('Error fetching road map data:', err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const resetApiFilters = () => {
    setApiFilterPlatform('');
    setApiFilterRoadmap('all');
    setApiFilterMinOrigin('');
    setApiFilterDateFrom('');
    setApiFilterDateTo('');
    setApiFilterStaleOnly(false);
    setApiFilterUserType('');
    setApiFilterPlayerId('');
    setApiPlayerSearchInput('');
    setCurrentPage(1);
  };

  const submitApiPlayerSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const q = apiPlayerSearchInput.trim();
    setApiFilterPlayerId(q);
    setCurrentPage(1);
  };

  const clearApiPlayerSearch = () => {
    setApiPlayerSearchInput('');
    setApiFilterPlayerId('');
    setCurrentPage(1);
  };

  // Fetch player details when accordion is opened
  const fetchPlayerDetails = async (playerId: string) => {
    // If already loaded, don't fetch again
    if (playerDetails[playerId]) {
      return;
    }

    try {
      setLoadingDetails((prev) => new Set(prev).add(playerId));

      const response = await dashboardService.getRoadmapDetail(playerId);

      setPlayerDetails((prev) => ({
        ...prev,
        [playerId]: response.data || [],
      }));
    } catch (err: any) {
      console.error(`Error fetching details for player ${playerId}:`, getUserFriendlyErrorMessage(err), err);
      // Set empty array on error
      setPlayerDetails((prev) => ({
        ...prev,
        [playerId]: [],
      }));
    } finally {
      setLoadingDetails((prev) => {
        const newSet = new Set(prev);
        newSet.delete(playerId);
        return newSet;
      });
    }
  };

  const apiFilterKey = useMemo(() => JSON.stringify(apiFilterCfg), [apiFilterCfg]);
  const prevApiFilterKey = useRef(apiFilterKey);

  useEffect(() => {
    if (prevApiFilterKey.current !== apiFilterKey) {
      prevApiFilterKey.current = apiFilterKey;
      if (currentPage !== 1) {
        setCurrentPage(1);
        return;
      }
    }
    void fetchRoadMapData(currentPage, apiFilterCfg);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch when page or filters change
  }, [currentPage, apiFilterKey]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    void fetchRoadMapData(currentPage, apiFilterCfg);
    if (ROADMAP_FIREBASE_UI_ENABLED && firebaseViewActive) {
      setFirebaseAllNewCache(null);
      setFirebaseReloadNonce((n) => n + 1);
    }
  };

  const togglePlayer = (playerId: string) => {
    setExpandedPlayers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(playerId)) {
        newSet.delete(playerId);
      } else {
        newSet.add(playerId);
        // Fetch details when opening
        fetchPlayerDetails(playerId);
      }
      return newSet;
    });
  };

  const toggleFirebasePlayer = (playerId: string) => {
    setExpandedFirebasePlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  // Prepare chart data for a player
  const getChartData = (details: RoadmapDetailItem[]) => {
    if (!details || details.length === 0) return [];

    // Sort by created_at
    const sorted = [...details].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateA - dateB;
    });

    // Prepare data for chart - include seconds to make each point unique
    // X-axis: date, Y-axis: origin only
    return sorted.map((item, index) => {
      const date = new Date(item.created_at);
      // Include seconds to make each data point unique
      const dateStr = date.toLocaleDateString('tr-TR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

      return {
        name: dateStr,
        origin: item.origin,
        index: index + 1,
        // Store original item for tooltip accuracy
        originalItem: item,
      };
    });
  };

  const formatDash = (v: string | number | boolean | null | undefined): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Evet' : 'Hayır';
    return String(v);
  };

  const formatApiDetailDate = (detail: RoadmapDetailItem): string => {
    const raw = detail.meta?.completed_at || detail.created_at;
    try {
      return new Date(raw).toLocaleString('tr-TR', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const metaVal = (m: RoadmapDetailMeta | undefined, key: keyof RoadmapDetailMeta): string | number | boolean | null => {
    if (!m) return null;
    const v = m[key];
    return v === undefined ? null : (v as string | number | boolean | null);
  };

  const getFirebaseChartData = (events: RoadmapFirestoreEvent[]) => {
    if (!events?.length) return [];
    const sorted = [...events].sort(
      (a, b) => (a.completed_at?.getTime() ?? 0) - (b.completed_at?.getTime() ?? 0)
    );
    return sorted.map((item, index) => {
      const date = item.completed_at ?? new Date(0);
      const dateStr = date.toLocaleDateString('tr-TR', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      return {
        name: dateStr,
        origin: item.origin ?? 0,
        index: index + 1,
        originalItem: item,
      };
    });
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6 p-6">
      <div className="mb-8">
        <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 dark:from-slate-200 dark:to-slate-400 bg-clip-text text-transparent mb-2">
          Road Map
        </h1>
        <p className="text-slate-600 dark:text-slate-400">Kullanıcıların görev ilerlemesini takip edin</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-gray-100 dark:border-gray-700">
        <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Kullanıcı Road Map</h2>
          <div className="flex flex-wrap items-center gap-2">
            {ROADMAP_FIREBASE_UI_ENABLED && firebaseViewActive && (
              <button
                type="button"
                onClick={clearFirebaseView}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                API road map
              </button>
            )}
            {ROADMAP_FIREBASE_UI_ENABLED && (
              <button
                type="button"
                onClick={handleFetchFirebase}
                disabled={firebaseListLoading}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-500 hover:bg-amber-600 border border-amber-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {renderIcon(SiFirebase, `w-4 h-4 ${firebaseListLoading ? 'animate-pulse' : ''}`)}
                <span>Firebase</span>
              </button>
            )}
          <button
            onClick={handleRefresh}
            disabled={isRefreshing || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {renderIcon(FiRefreshCw, `w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`)}
            <span>Yenile</span>
          </button>
          </div>
        </div>

        {error && roadmapItems.length === 0 && !firebaseViewActive && (
          <div className="px-6 py-4 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {ROADMAP_FIREBASE_UI_ENABLED && firebaseError && (
          <div className="px-6 py-4 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 text-amber-900 dark:text-amber-200 text-sm">
              <span>Firebase</span>
              <span className="font-mono break-all">{firebaseError}</span>
            </div>
          </div>
        )}

        <div className="p-6 space-y-6">
          {!firebaseViewActive && (
            <RoadmapApiFiltersPanel
              apiFilterPlatform={apiFilterPlatform}
              setApiFilterPlatform={setApiFilterPlatform}
              apiFilterRoadmap={apiFilterRoadmap}
              setApiFilterRoadmap={setApiFilterRoadmap}
              apiFilterMinOrigin={apiFilterMinOrigin}
              setApiFilterMinOrigin={setApiFilterMinOrigin}
              apiFilterDateFrom={apiFilterDateFrom}
              setApiFilterDateFrom={setApiFilterDateFrom}
              apiFilterDateTo={apiFilterDateTo}
              setApiFilterDateTo={setApiFilterDateTo}
              apiFilterStaleOnly={apiFilterStaleOnly}
              setApiFilterStaleOnly={setApiFilterStaleOnly}
              apiFilterUserType={apiFilterUserType}
              setApiFilterUserType={setApiFilterUserType}
              apiPlayerSearchInput={apiPlayerSearchInput}
              setApiPlayerSearchInput={setApiPlayerSearchInput}
              apiFilterPlayerId={apiFilterPlayerId}
              apiFiltersActive={apiFiltersActive}
              loading={loading}
              pagination={pagination}
              currentPage={currentPage}
              onPageOne={() => setCurrentPage(1)}
              onReset={resetApiFilters}
              onSubmitPlayerSearch={submitApiPlayerSearch}
              onClearPlayerSearch={clearApiPlayerSearch}
              renderIcon={renderIcon}
            />
          )}

          {loading && !firebaseViewActive && (
            <div className="rounded-xl border border-indigo-300/80 bg-indigo-50/70 px-4 py-3 text-indigo-900 dark:border-indigo-800 dark:bg-indigo-950/25 dark:text-indigo-100">
              <div className="flex items-center gap-2.5">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent dark:border-indigo-300" />
                <span className="text-sm font-medium animate-pulse">Liste yükleniyor…</span>
              </div>
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-indigo-200/80 dark:bg-indigo-900/60">
                <div className="h-full w-1/3 animate-pulse rounded-full bg-indigo-500" />
              </div>
            </div>
          )}

          {loading && roadmapItems.length === 0 && !firebaseViewActive ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : (
            <div className="space-y-10">
              {roadmapItems.length === 0 && !firebaseViewActive ? (
                <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                  {apiFiltersActive ? 'Filtrelere uyan kullanıcı bulunamadı' : 'Kullanıcı bulunamadı'}
                </div>
              ) : roadmapItems.length > 0 && !firebaseViewActive ? (
            <div className="space-y-4">
              {roadmapItems.map((item, index) => {
                const isExpanded = expandedPlayers.has(item.player_id);
                const details = playerDetails[item.player_id] || [];
                const isLoadingDetails = loadingDetails.has(item.player_id);
                const chartData = getChartData(details);
                
                // Calculate progress: origin / destination * 100
                const maxValue = item.destination || 1; // Max value is destination
                const currentValue = item.origin || 0;
                const progressPercentage = maxValue > 0 ? Math.min((currentValue / maxValue) * 100, 100) : 0;

                return (
                  <div
                    key={`${item.player_id}-${index}`}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                  >
                    {/* Player Header */}
                    <button
                      onClick={() => togglePlayer(item.player_id)}
                      className="w-full px-4 py-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                    >
                      <div className="flex items-center gap-3 mb-3">
                        {renderIcon(
                          isExpanded ? FiChevronDown : FiChevronRight,
                          "w-5 h-5 text-slate-600 dark:text-slate-400 flex-shrink-0"
                        )}
                        <div className="text-left flex-1 min-w-0">
                          <div className="font-semibold text-slate-800 dark:text-white truncate">
                            Player ID: <span className="font-mono text-sm">{item.player_id}</span>
                          </div>
                          <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                            Versiyon: {item.app_version}
                          </div>
                        </div>
                      </div>
                      
                      {/* Progress Slider */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            {renderIcon(FiTarget, "w-4 h-4 text-indigo-600 dark:text-indigo-400")}
                            <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                              Origin: {currentValue}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {renderIcon(FiCheckCircle, "w-4 h-4 text-purple-600 dark:text-purple-400")}
                            <span className="font-semibold text-purple-600 dark:text-purple-400">
                              Destination: {maxValue}
                            </span>
                          </div>
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden">
                          <div
                            className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                            style={{ width: `${progressPercentage}%` }}
                          >
                            {progressPercentage > 10 && (
                              <span className="text-xs font-semibold text-white">
                                {Math.round(progressPercentage)}%
                              </span>
                            )}
                          </div>
                        </div>
                        {progressPercentage <= 10 && (
                          <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                            {Math.round(progressPercentage)}%
                          </div>
                        )}
                      </div>
                    </button>

                    {/* Player Content */}
                    {isExpanded && (
                      <div className="p-4 bg-white dark:bg-gray-800">
                        {isLoadingDetails ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : details.length === 0 ? (
                          <div className="text-center py-12 text-slate-500 dark:text-slate-400">
                            Bu kullanıcı için veri bulunamadı
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Line Graph */}
                            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                              <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                                İlerleme Grafiği
                              </h3>
                              <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                  <CartesianGrid 
                                    strokeDasharray="3 3" 
                                    stroke={document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'} 
                                  />
                                  <XAxis 
                                    dataKey="name" 
                                    stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'}
                                    angle={-45}
                                    textAnchor="end"
                                    height={80}
                                  />
                                  <YAxis 
                                    domain={[0, 'dataMax + 10']} 
                                    stroke={document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'} 
                                  />
                                  <Legend 
                                    wrapperStyle={{
                                      color: document.documentElement.classList.contains('dark') ? '#f3f4f6' : '#111827',
                                    }}
                                    formatter={() => 'Origin'}
                                  />
                                  <Line 
                                    type="monotone" 
                                    dataKey="origin" 
                                    stroke="#3b82f6" 
                                    strokeWidth={2} 
                                    dot={{ r: 4 }} 
                                    name="origin"
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Details Table — meta alanları Firebase satırlarıyla aynı düzen */}
                            <div className="overflow-x-auto">
                              <table className="w-full min-w-[1100px]">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Tarih
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Versiyon
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Görev kodu
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Görev
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Tip
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Sahne
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Quiz ID
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Chatbot
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Min. seviye
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Seans
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Seans başlığı
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      {"Roadmap'ten"}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Origin
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Destination
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Platform
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                      Cihaz
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                  {[...details]
                                    .sort(
                                      (a, b) =>
                                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                                    )
                                    .map((detail, detailIndex) => {
                                      const m = detail.meta;
                                      const crm = m?.completed_from_roadmap;
                                      return (
                                        <tr
                                          key={`${detail.player_id}-${detailIndex}-${detail.created_at}`}
                                          className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        >
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatApiDetailDate(detail)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {detail.app_version}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'task_code') as string | null)}
                                          </td>
                                          <td
                                            className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[200px] truncate"
                                            title={String(metaVal(m, 'task_title') ?? '')}
                                          >
                                            {formatDash(metaVal(m, 'task_title') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'task_type') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'task_scene') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'quiz_id') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'chatbot_interaction_type') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {m?.required_level != null ? m.required_level : '—'}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {m?.session_no != null ? m.session_no : '—'}
                                          </td>
                                          <td
                                            className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[160px] truncate"
                                            title={String(metaVal(m, 'session_title') ?? '')}
                                          >
                                            {formatDash(metaVal(m, 'session_title') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {crm === true ? 'Evet' : crm === false ? 'Hayır' : '—'}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                            {detail.origin}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-purple-600 dark:text-purple-400">
                                            {detail.destination}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'platform') as string | null)}
                                          </td>
                                          <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                            {formatDash(metaVal(m, 'device_model') as string | null)}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
              ) : null}

              {ROADMAP_FIREBASE_UI_ENABLED && firebaseViewActive && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-1">
                      Firestore — roadmap_progress ({ROADMAP_PROGRESS_PAGE_SIZE} kayıt / sayfa)
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 flex flex-wrap gap-x-4 gap-y-1">
                      <span>
                        <strong className="text-slate-800 dark:text-slate-200">{firebaseStats.total}</strong> görev
                        kaydı
                      </span>
                      <span>
                        <strong className="text-slate-800 dark:text-slate-200">{firebaseStats.uniquePlayers}</strong>{' '}
                        oyuncu (bu sayfa / arama)
                      </span>
                      {!firebasePlayerFromUrl && (
                        <span className="text-amber-800 dark:text-amber-200">
                          {firebaseClientTotalPages != null ? (
                            <>
                              Sayfa <strong>{firebasePageFromUrl}</strong> /{' '}
                              <strong>{firebaseClientTotalPages}</strong>
                              {' · '}
                              <strong>{firebaseClientFilteredTotal}</strong> eşleşme (tüm New)
                            </>
                          ) : (
                            <>
                              Sayfa <strong>{firebasePageFromUrl}</strong>
                              {firebaseHasNextPage ? ' · sonraki sayfa var' : ' · son sayfa'}
                            </>
                          )}
                        </span>
                      )}
                      <span>
                        Roadmap:{' '}
                        <strong className="text-slate-800 dark:text-slate-200">{firebaseStats.fromRoadMap}</strong>
                      </span>
                      <span>
                        Dışarı:{' '}
                        <strong className="text-slate-800 dark:text-slate-200">{firebaseStats.fromOutside}</strong>
                      </span>
                      {firebaseStats.unknownOrigin > 0 && (
                        <span>
                          Bilinmeyen:{' '}
                          <strong className="text-slate-800 dark:text-slate-200">{firebaseStats.unknownOrigin}</strong>
                        </span>
                      )}
                      <span>
                        <strong className="text-slate-800 dark:text-slate-200">{filteredProgressPlayers.length}</strong>
                        /{progressPlayers.length} oyuncu (filtre sonrası)
                      </span>
                    </p>
                    <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                      <form
                        onSubmit={submitFirebasePlayerSearch}
                        className="flex min-w-0 flex-1 flex-col gap-1.5 sm:max-w-xl"
                      >
                        <label
                          htmlFor="fb-player-search"
                          className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                        >
                          Player ID ara
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <input
                            id="fb-player-search"
                            type="search"
                            autoComplete="off"
                            value={firebasePlayerSearchInput}
                            onChange={(e) => setFirebasePlayerSearchInput(e.target.value)}
                            placeholder="Tam eşleşme (UUID)"
                            className="min-w-0 flex-1 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm font-mono text-slate-800 dark:text-slate-100 shadow-sm placeholder:text-slate-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/25 dark:focus:border-amber-500"
                          />
                          <button
                            type="submit"
                            disabled={firebaseListLoading || !firebasePlayerSearchInput.trim()}
                            className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-amber-600 disabled:opacity-50 dark:border-amber-600 dark:bg-amber-600 dark:hover:bg-amber-500"
                          >
                            {renderIcon(FiSearch, 'h-4 w-4')}
                            Ara
                          </button>
                          {firebasePlayerFromUrl ? (
                            <button
                              type="button"
                              onClick={clearFirebasePlayerSearch}
                              disabled={firebaseListLoading}
                              className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                            >
                              Listeye dön
                            </button>
                          ) : null}
                        </div>
                      </form>
                      {!firebasePlayerFromUrl ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setFirebasePageUrl(firebasePageFromUrl - 1)}
                            disabled={firebasePageFromUrl <= 1 || firebaseListLoading}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                          >
                            Önceki sayfa
                          </button>
                          <span className="px-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                            Sayfa {firebasePageFromUrl}
                            {firebaseClientTotalPages != null ? ` / ${firebaseClientTotalPages}` : ''}
                          </span>
                          <button
                            type="button"
                            onClick={() => setFirebasePageUrl(firebasePageFromUrl + 1)}
                            disabled={!firebaseHasNextPage || firebaseListLoading}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40 dark:border-slate-600 dark:bg-gray-800 dark:text-slate-200 dark:hover:bg-gray-700"
                          >
                            Sonraki sayfa
                          </button>
                        </div>
                      ) : null}
                    </div>
                    <div className="mt-5 rounded-2xl border border-slate-200/90 dark:border-slate-600/80 bg-gradient-to-br from-white via-slate-50/95 to-amber-50/25 dark:from-gray-900 dark:via-gray-900 dark:to-amber-950/25 shadow-sm overflow-hidden ring-1 ring-slate-900/5 dark:ring-white/5">
                      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-slate-200/80 dark:border-slate-700/80 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400 to-amber-600 text-white shadow-md shadow-amber-500/25">
                            {renderIcon(FiFilter, 'h-5 w-5')}
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-sm font-semibold text-slate-800 dark:text-white tracking-tight">
                              Filtreler
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                              Yüklenen oyuncu listesini daraltın. Roadmap Evet/Hayır: kartta yalnızca seçilen tipe ait görev
                              satırları; diğer tip gizlenir.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={resetFirebaseFilters}
                          disabled={!firebaseFiltersActive}
                          className="shrink-0 inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-gray-700 hover:border-slate-300 dark:hover:border-slate-500 disabled:opacity-40 disabled:pointer-events-none disabled:hover:bg-white dark:disabled:hover:bg-gray-800 transition-colors"
                        >
                          Tümünü sıfırla
                        </button>
                      </div>
                      <div className="p-5 sm:p-6">
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                          <RoadmapFilterDropdown
                            label="Platform"
                            labelId="fb-filter-platform"
                            value={firebaseFilterPlatform}
                            onChange={setFirebaseFilterPlatform}
                            placeholder="Tüm platformlar"
                            options={[
                              { value: '', label: 'Tüm platformlar' },
                              ...firebasePlatformOptions.map((plat) => ({ value: plat, label: plat })),
                            ]}
                          />
                          <RoadmapFilterDropdown
                            label={"Roadmap'ten"}
                            labelId="fb-filter-roadmap"
                            value={firebaseFilterRoadmap}
                            onChange={(v) => setFirebaseFilterRoadmap(v as 'all' | 'yes' | 'no')}
                            placeholder="Tüm kayıtlar"
                            options={ROADMAP_SOURCE_FILTER_OPTIONS}
                          />
                          <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
                            <label
                              htmlFor="fb-filter-origin"
                              className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                            >
                              Min. origin (≥)
                            </label>
                            <input
                              id="fb-filter-origin"
                              type="number"
                              min={0}
                              inputMode="numeric"
                              placeholder="ör. 61"
                              value={firebaseFilterMinRoadmapOrigin}
                              onChange={(e) => setFirebaseFilterMinRoadmapOrigin(e.target.value)}
                              className="block w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 px-3.5 py-2.5 text-sm text-slate-800 dark:text-slate-100 shadow-sm placeholder:text-slate-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/25 dark:focus:border-amber-500 transition-colors"
                            />
                            <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-500">
                              Sadece{" "}
                              <span className="font-medium text-slate-600 dark:text-slate-400">
                                Roadmap&apos;ten: Evet
                              </span>{" "}
                              görevlerindeki en yüksek origin; 60&apos;dan büyük için{" "}
                              <span className="font-mono font-medium text-slate-700 dark:text-slate-300">61</span>{" "}
                              yazın.
                            </p>
                          </div>
                        </div>
                        <div className="mt-5 grid grid-cols-1 gap-4 border-t border-slate-200/80 pt-5 dark:border-slate-700/80 sm:grid-cols-2">
                          <div className="space-y-1.5">
                            <label
                              htmlFor="fb-filter-date-from"
                              className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                            >
                              Tamamlanma — başlangıç
                            </label>
                            <input
                              id="fb-filter-date-from"
                              type="date"
                              value={firebaseFilterDateFrom}
                              onChange={(e) => setFirebaseFilterDateFrom(e.target.value)}
                              className="block h-[42px] w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 px-3.5 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/25 dark:focus:border-amber-500"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label
                              htmlFor="fb-filter-date-to"
                              className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400"
                            >
                              Tamamlanma — bitiş
                            </label>
                            <input
                              id="fb-filter-date-to"
                              type="date"
                              value={firebaseFilterDateTo}
                              onChange={(e) => setFirebaseFilterDateTo(e.target.value)}
                              className="block h-[42px] w-full rounded-xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 px-3.5 text-sm text-slate-800 dark:text-slate-100 shadow-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/25 dark:focus:border-amber-500"
                            />
                          </div>
                        </div>
                        <p className="mt-2 text-[11px] leading-relaxed text-slate-500 dark:text-slate-500">
                          Liste ve kart içi tablo / grafik yalnızca görev{" "}
                          <span className="font-medium text-slate-600 dark:text-slate-400">completed_at</span> bu
                          aralığa düşen oyuncuları gösterir; aralıktan sonra eklenenler için bitişi boş bırakıp
                          sadece başlangıç seçin.
                        </p>
                        <div className="mt-5 flex items-start gap-3 rounded-xl border border-rose-200/90 bg-gradient-to-r from-rose-50/90 to-amber-50/30 px-4 py-3.5 dark:border-rose-900/45 dark:from-rose-950/35 dark:to-amber-950/20">
                          <input
                            id="fb-filter-stale-only"
                            type="checkbox"
                            checked={firebaseFilterStaleOnly}
                            onChange={(e) => setFirebaseFilterStaleOnly(e.target.checked)}
                            className="mt-0.5 h-4 w-4 shrink-0 rounded border-rose-300 text-rose-600 focus:ring-2 focus:ring-rose-500/30 dark:border-rose-700 dark:bg-gray-900 dark:text-rose-500"
                          />
                          <label
                            htmlFor="fb-filter-stale-only"
                            className="min-w-0 cursor-pointer text-sm leading-snug text-rose-950 dark:text-rose-50"
                          >
                            <span className="font-semibold">
                              Sadece{" "}
                              <span
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-[11px] font-black text-white align-middle mx-0.5"
                                aria-hidden
                              >
                                !
                              </span>{" "}
                              uyarılı oyuncular
                            </span>
                            <span className="mt-1 block text-xs font-normal text-rose-900/85 dark:text-rose-200/90">
                              Son görev tamamlanması {STALE_INACTIVE_DAYS} günden eski; görev yoksa veya tarih yoksa da
                              listelenir (kartta kırmızı ünlem).
                            </span>
                          </label>
                        </div>
                        <div className="mt-5 flex gap-2.5 rounded-xl bg-slate-100/80 dark:bg-slate-800/60 px-3.5 py-3 text-xs text-slate-600 dark:text-slate-400">
                          {renderIcon(FiInfo, 'h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400 mt-0.5')}
                          <span className="leading-relaxed">
                            Aktif filtreler üstteki sayılara yansır. Sonuç yoksa koşulları gevşetin veya
                            &quot;Tümünü sıfırla&quot; ile başa dönün.
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {firebaseListLoading && (
                      <div className="rounded-xl border border-amber-300/80 bg-amber-50/70 px-4 py-3 text-amber-900 dark:border-amber-800 dark:bg-amber-950/25 dark:text-amber-100">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2.5">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-amber-600 border-t-transparent dark:border-amber-300" />
                            <span className="text-sm font-medium animate-pulse">
                              {firebaseFiltersActive
                                ? 'Filtre uygulanıyor, sonuçlar güncelleniyor...'
                                : 'Firebase verileri yükleniyor...'}
                            </span>
                          </div>
                          <span className="text-xs font-semibold uppercase tracking-wide opacity-80">
                            Tahmini 5-20 sn
                          </span>
                        </div>
                        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-amber-200/80 dark:bg-amber-900/60">
                          <div className="h-full w-1/3 animate-[pulse_1.2s_ease-in-out_infinite] rounded-full bg-amber-500 dark:bg-amber-400" />
                        </div>
                      </div>
                    )}
                    {firebaseListLoading && progressPlayers.length === 0 && (
                      <div className="flex items-center justify-center gap-3 rounded-xl border border-dashed border-amber-300 bg-amber-50/40 py-14 dark:border-amber-800 dark:bg-amber-950/20">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-500 border-t-transparent" />
                        <span className="text-sm font-medium text-amber-900 dark:text-amber-100">Firestore yükleniyor…</span>
                      </div>
                    )}
                    {firebasePlayerFromUrl && !firebaseListLoading && progressPlayers.length === 0 && (
                      <div className="text-center py-12 px-4 rounded-xl border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 text-slate-600 dark:text-slate-400 text-sm">
                        Bu Player ID için <code className="font-mono text-xs">status: New</code> kaydı bulunamadı veya
                        döküman yolu eşleşmedi.
                      </div>
                    )}
                    {!firebaseListLoading && progressPlayers.length > 0 && filteredProgressPlayers.length === 0 && (
                      <div className="text-center py-12 px-4 rounded-xl border border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 text-slate-600 dark:text-slate-400 text-sm">
                        Filtrelere uyan oyuncu yok. Koşulları gevşetin veya &quot;Filtreleri sıfırla&quot; kullanın.
                      </div>
                    )}
                    {progressPlayersSorted.map((player, index) => {
                      const playerId = player.player_id;
                      const eventsRaw = firebaseByPlayer.get(playerId) ?? [];
                      const events = filterEventsByRoadmapSource(
                        filterEventsByCompletedAtRange(
                          eventsRaw,
                          firebaseFilterDateFrom,
                          firebaseFilterDateTo
                        ),
                        firebaseFilterRoadmap
                      );
                      const fromRoadmapOnly = events.filter((e) => e.completed_from_roadmap === true);
                      const maxOrigin =
                        fromRoadmapOnly.length > 0
                          ? Math.max(0, ...fromRoadmapOnly.map((e) => e.origin ?? 0))
                          : 0;
                      const atPeakOrigin = fromRoadmapOnly.filter((e) => (e.origin ?? 0) === maxOrigin);
                      const peakEvent =
                        atPeakOrigin.length > 0
                          ? atPeakOrigin.reduce((a, b) =>
                              (b.completed_at?.getTime() ?? 0) >= (a.completed_at?.getTime() ?? 0) ? b : a
                            )
                          : undefined;
                      const currentValue = maxOrigin;
                      const maxValue =
                        fromRoadmapOnly.length > 0
                          ? Math.max(
                              1,
                              peakEvent?.destination ?? 0,
                              ...fromRoadmapOnly.map((e) => e.destination ?? 0)
                            )
                          : 1;
                      const progressPercentage =
                        maxValue > 0 ? Math.min((currentValue / maxValue) * 100, 100) : 0;
                      const isExpanded = expandedFirebasePlayers.has(playerId);
                      const chartData = getFirebaseChartData(events);
                      const showStaleBadge = isPlayerInactive10Days(player);
                      const lastActMs = lastPlayerActivityMs(player);
                      const staleTitle =
                        lastActMs > 0
                          ? `Son aktivite: ${new Date(lastActMs).toLocaleString('tr-TR')} — ${STALE_INACTIVE_DAYS}+ gün önce`
                          : `Son aktivite tarihi yok — ${STALE_INACTIVE_DAYS}+ gün kuralı için uyarılı`;

                      return (
                        <div
                          key={`fb-${playerId}-${index}`}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
                        >
                          <button
                            type="button"
                            onClick={() => toggleFirebasePlayer(playerId)}
                            className="w-full px-4 py-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors text-left"
                          >
                            <div className="flex items-center gap-3 mb-3">
                              {renderIcon(
                                isExpanded ? FiChevronDown : FiChevronRight,
                                'w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0'
                              )}
                              <div className="text-left flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-slate-800 dark:text-white">
                                  <span className="truncate">
                                    Player ID: <span className="font-mono text-sm">{playerId}</span>
                                  </span>
                                  {showStaleBadge && (
                                    <span
                                      className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-600 text-sm font-black leading-none text-white shadow-md shadow-rose-600/25 ring-2 ring-white dark:ring-gray-800"
                                      title={staleTitle}
                                      aria-label={`Uyarı: son görev ${STALE_INACTIVE_DAYS} günden eski`}
                                    >
                                      !
                                    </span>
                                  )}
                                  <span className="text-xs font-normal text-slate-500 dark:text-slate-400">
                                    ({events.length} kayıt)
                                  </span>
                                </div>
                                <div className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                                  Sürüm: {formatDash(player.app_version)}
                                  {player.platform ? ` · ${player.platform}` : ''}
                                  {player.device_model ? ` · ${player.device_model}` : ''}
                                </div>
                                {player.created_at && (
                                  <div className="text-xs text-slate-500 dark:text-slate-500 mt-0.5">
                                    Kayıt:{' '}
                                    {player.created_at.toLocaleString('tr-TR', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                  {renderIcon(FiTarget, 'w-4 h-4 text-indigo-600 dark:text-indigo-400')}
                                  <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                                    Origin: {currentValue}
                                    {fromRoadmapOnly.length > 0 && (
                                      <span className="ml-1 font-normal text-xs text-slate-500 dark:text-slate-400">
                                        (roadmap&apos;ten, en yüksek)
                                      </span>
                                    )}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  {renderIcon(FiCheckCircle, 'w-4 h-4 text-purple-600 dark:text-purple-400')}
                                  <span className="font-semibold text-purple-600 dark:text-purple-400">
                                    Destination: {maxValue}
                                  </span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden">
                                <div
                                  className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out flex items-center justify-end pr-2"
                                  style={{ width: `${progressPercentage}%` }}
                                >
                                  {progressPercentage > 10 && (
                                    <span className="text-xs font-semibold text-white">
                                      {Math.round(progressPercentage)}%
                                    </span>
                                  )}
                                </div>
                              </div>
                              {progressPercentage <= 10 && (
                                <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                                  {Math.round(progressPercentage)}%
                                </div>
                              )}
                              {events.length > 0 && fromRoadmapOnly.length === 0 && (
                                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                  Roadmap&apos;ten tamamlanan kayıt yok; üst özet 0 gösterilir.
                                </p>
                              )}
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="p-4 bg-white dark:bg-gray-800">
                              <div className="space-y-4">
                                <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
                                  <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-4">
                                    İlerleme Grafiği
                                  </h3>
                                  <ResponsiveContainer width="100%" height={300}>
                                    <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                      <CartesianGrid
                                        strokeDasharray="3 3"
                                        stroke={
                                          document.documentElement.classList.contains('dark') ? '#374151' : '#e5e7eb'
                                        }
                                      />
                                      <XAxis
                                        dataKey="name"
                                        stroke={
                                          document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'
                                        }
                                        angle={-45}
                                        textAnchor="end"
                                        height={80}
                                      />
                                      <YAxis
                                        domain={[0, 'dataMax + 10']}
                                        stroke={
                                          document.documentElement.classList.contains('dark') ? '#9ca3af' : '#6b7280'
                                        }
                                      />
                                      <Legend
                                        wrapperStyle={{
                                          color: document.documentElement.classList.contains('dark')
                                            ? '#f3f4f6'
                                            : '#111827',
                                        }}
                                        formatter={() => 'Origin'}
                                      />
                                      <Line
                                        type="monotone"
                                        dataKey="origin"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={{ r: 4 }}
                                        name="origin"
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>

                                <div className="overflow-x-auto">
                                  <table className="w-full min-w-[1100px]">
                                    <thead className="bg-gray-50 dark:bg-gray-700">
                                      <tr>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Tarih
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Versiyon
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Görev kodu
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Görev
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Tip
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Sahne
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Quiz ID
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Chatbot
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Min. seviye
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Seans
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Seans başlığı
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          {"Roadmap'ten"}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Origin
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Destination
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider whitespace-nowrap">
                                          Platform
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                      {[...events]
                                        .sort(
                                          (a, b) =>
                                            (b.completed_at?.getTime() ?? 0) - (a.completed_at?.getTime() ?? 0)
                                        )
                                        .map((row, detailIndex) => {
                                          const formattedDate = row.completed_at
                                            ? row.completed_at.toLocaleString('tr-TR', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                              })
                                            : '—';
                                    return (
                                      <tr
                                              key={`${playerId}-fb-${detailIndex}-${row.completed_at?.toISOString()}`}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                      >
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                          {formattedDate}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.app_version)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.task_code)}
                                              </td>
                                              <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[200px] truncate" title={row.task_title ?? ''}>
                                                {formatDash(row.task_title)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.task_type)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.task_scene)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.quiz_id)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.chatbot_interaction_type)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {row.required_level != null ? row.required_level : '—'}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {row.session_no != null ? row.session_no : '—'}
                                              </td>
                                              <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300 max-w-[160px] truncate" title={row.session_title ?? ''}>
                                                {formatDash(row.session_title)}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {row.completed_from_roadmap === true
                                                  ? 'Evet'
                                                  : row.completed_from_roadmap === false
                                                    ? 'Hayır'
                                                    : '—'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-indigo-600 dark:text-indigo-400">
                                                {row.origin ?? '—'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-purple-600 dark:text-purple-400">
                                                {row.destination ?? '—'}
                                              </td>
                                              <td className="px-4 py-3 whitespace-nowrap text-sm text-slate-600 dark:text-slate-300">
                                                {formatDash(row.platform)}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                      </div>
                    )}
                  </div>
                );
              })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination — sadece API road map görünürken */}
        {pagination.totalPages > 1 && !firebaseViewActive && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="text-sm text-slate-600 dark:text-slate-400">
              Toplam {pagination.total} kayıttan sayfa {currentPage} / {pagination.totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Önceki
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  let pageNum: number;
                  if (pagination.totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= pagination.totalPages - 2) {
                    pageNum = pagination.totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      disabled={loading}
                      className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-indigo-600 dark:bg-indigo-500 text-white'
                          : 'text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages || loading}
                className="px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Sonraki
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoadMap;
